import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  PaperPlaneTilt, ArrowLeft, ChatCircleDots, PencilSimple, Trash, Check, X,
  Microphone, Image as ImageIcon, Smiley, Stop, Storefront,
} from "@phosphor-icons/react";
import { uploadBlob } from "@/lib/blob-upload";
import { resolveMedia } from "@/lib/media";
import { useAdaptiveInterval } from "@/context/DataSaverContext";

const EMOJI_SET = ["heart", "fire", "clap", "laugh", "wow", "sad"];
const EMOJI_CHARS = {
  heart: "\u2764\ufe0f",
  fire: "\ud83d\udd25",
  clap: "\ud83d\udc4f",
  laugh: "\ud83d\ude02",
  wow: "\ud83d\ude32",
  sad: "\ud83d\ude22",
};

function ThreadList({ threads, activeId, onSelect }) {
  if (threads.length === 0) {
    return (
      <div className="p-6 text-center text-white/50 text-sm">
        No conversations yet. Send a message from any post author menu.
      </div>
    );
  }
  return (
    <div className="divide-y divide-white/5">
      {threads.map((t) => (
        <button
          key={t.other_user.user_id}
          data-testid={`thread-${t.other_user.user_id}`}
          onClick={() => onSelect(t.other_user.user_id)}
          className={`w-full text-left px-4 py-3 hover:bg-white/5 transition-colors flex items-center gap-3 ${
            activeId === t.other_user.user_id ? "bg-white/[0.06]" : ""
          }`}
        >
          {t.other_user.picture ? (
            <img src={t.other_user.picture} alt="" className="w-11 h-11 rounded-full" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-white/10" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="font-heading font-bold truncate">{t.other_user.name}</p>
              {t.unread > 0 && (
                <span className="text-[10px] px-1.5 h-4 rounded-full bg-red-500 text-white font-bold flex items-center">
                  {t.unread}
                </span>
              )}
            </div>
            <p className="text-xs text-white/60 truncate mt-0.5">
              {t.last_from_me ? "You: " : ""}
              {t.last_text || "\u2026"}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

function Conversation({ otherUserId, onBack, refreshThreads }) {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [recorder, setRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [reactionPickerFor, setReactionPickerFor] = useState(null);
  const bottomRef = useRef(null);
  const imgInputRef = useRef(null);
  const chunksRef = useRef([]);
  const recordStartRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/${otherUserId}`);
      setThread(data);
      refreshThreads?.();
    } catch {
      toast.error("Failed to load messages");
    }
  }, [otherUserId, refreshThreads]);

  useEffect(() => { load(); }, [load]);
  useAdaptiveInterval(load, 15000);  // Conversation poll: was 8s → 15s

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/messages/${otherUserId}`, { text: text.trim() });
      setText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const startEdit = (m) => { setEditingId(m.message_id); setEditText(m.text || ""); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };
  const saveEdit = async () => {
    if (!editText.trim() || !editingId) return;
    try {
      await api.put(`/messages/msg/${editingId}`, { text: editText });
      setThread((t) =>
        t ? {
          ...t,
          messages: t.messages.map((m) =>
            m.message_id === editingId ? { ...m, text: editText.trim(), edited_at: new Date().toISOString() } : m
          ),
        } : t
      );
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to edit");
    }
  };
  const removeMessage = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/messages/msg/${mid}`);
      setThread((t) => (t ? { ...t, messages: t.messages.filter((m) => m.message_id !== mid) } : t));
      refreshThreads?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  // ---- Voice recording ----
  const startRecording = async () => {
    if (recording) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Voice recording not supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const duration = Date.now() - recordStartRef.current;
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        try {
          setSending(true);
          const uploaded = await uploadBlob(blob, `voice-${Date.now()}.webm`);
          await api.post(`/messages/${otherUserId}`, {
            attachment_url: uploaded.path,
            attachment_type: "audio",
            duration_ms: duration,
          });
          load();
        } catch (e) {
          toast.error("Failed to send voice note");
        } finally {
          setSending(false);
        }
      };
      recordStartRef.current = Date.now();
      mr.start();
      setRecorder(mr);
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };
  const stopRecording = () => {
    if (recorder && recording) {
      recorder.stop();
      setRecording(false);
      setRecorder(null);
    }
  };

  // ---- Image share ----
  const handleImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image");
      return;
    }
    try {
      setSending(true);
      const uploaded = await uploadBlob(file, file.name);
      await api.post(`/messages/${otherUserId}`, {
        attachment_url: uploaded.path,
        attachment_type: "image",
      });
      load();
    } catch (e) {
      toast.error("Failed to send image");
    } finally {
      setSending(false);
      if (imgInputRef.current) imgInputRef.current.value = "";
    }
  };

  // ---- Reactions ----
  const react = async (mid, emojiKey) => {
    try {
      const { data } = await api.post(`/messages/msg/${mid}/react`, { emoji: emojiKey });
      setThread((t) => (t ? {
        ...t,
        messages: t.messages.map((m) => m.message_id === mid ? { ...m, reactions: data.reactions } : m),
      } : t));
      setReactionPickerFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (!thread) return <div className="p-6 text-white/60">Loading…</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-160px)] md:h-[calc(100vh-100px)]">
      <div className="flex items-center gap-3 p-4 border-b border-white/10">
        <button
          data-testid="conversation-back"
          onClick={onBack}
          className="md:hidden p-1 rounded hover:bg-white/10"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
        {thread.other_user.picture ? (
          <img src={thread.other_user.picture} alt="" className="w-9 h-9 rounded-full" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-white/10" />
        )}
        <Link to={`/u/${thread.other_user.user_id}`} className="font-heading font-bold hover:underline">
          {thread.other_user.name}
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2" onClick={() => setReactionPickerFor(null)}>
        {thread.messages.length === 0 ? (
          <p className="text-center text-white/50 text-sm py-8">Say hi</p>
        ) : (
          thread.messages.map((m) => {
            const mine = m.from_user_id !== otherUserId;
            const isEditing = editingId === m.message_id;
            const reactions = m.reactions || [];
            // Group reactions by emoji
            const grouped = reactions.reduce((acc, r) => {
              acc[r.emoji] = (acc[r.emoji] || 0) + 1;
              return acc;
            }, {});
            return (
              <div key={m.message_id} className={`group flex items-start ${mine ? "justify-end" : "justify-start"}`}>
                {mine && !isEditing && (
                  <div className="flex items-center gap-1 pr-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      data-testid={`msg-edit-${m.message_id}`}
                      onClick={() => startEdit(m)}
                      className="p-1 rounded hover:bg-white/10 text-white/60"
                    >
                      <PencilSimple size={13} weight="bold" />
                    </button>
                    <button
                      data-testid={`msg-delete-${m.message_id}`}
                      onClick={() => removeMessage(m.message_id)}
                      className="p-1 rounded hover:bg-red-500/20 text-red-400"
                    >
                      <Trash size={13} weight="bold" />
                    </button>
                  </div>
                )}
                {isEditing ? (
                  <div className="flex items-center gap-1 max-w-[75%]">
                    <Input
                      data-testid={`msg-edit-input-${m.message_id}`}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                      className="bg-white/5 border-white/10 text-white h-9 text-sm"
                      autoFocus
                    />
                    <button onClick={saveEdit} className="p-2 rounded-md bg-emerald-600 hover:bg-emerald-700">
                      <Check size={14} weight="bold" className="text-white" />
                    </button>
                    <button onClick={cancelEdit} className="p-2 rounded-md bg-white/10 hover:bg-white/20">
                      <X size={14} weight="bold" className="text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setReactionPickerFor(m.message_id); }}
                      className={`text-left max-w-[80vw] md:max-w-[26rem] px-3 py-2 rounded-2xl text-sm ${
                        mine
                          ? "bg-blue-600 text-white rounded-br-md"
                          : "bg-white/10 text-white rounded-bl-md"
                      }`}
                    >
                      {m.attachment_type === "image" && m.attachment_url && (
                        <img
                          src={resolveMedia(m.attachment_url)}
                          alt=""
                          className="max-w-[240px] max-h-64 rounded-md object-cover mb-1"
                        />
                      )}
                      {m.attachment_type === "audio" && m.attachment_url && (
                        <audio
                          controls
                          src={resolveMedia(m.attachment_url)}
                          className="max-w-[240px]"
                        />
                      )}
                      {m.text && <span>{m.text}</span>}
                      {m.edited_at && (
                        <span className="block text-[10px] opacity-60 mt-0.5">edited</span>
                      )}
                      {mine && (
                        <span
                          data-testid={`msg-read-${m.message_id}`}
                          className={`ml-1 inline-block align-middle text-[13px] leading-none ${m.read ? "text-sky-300" : "text-white/60"}`}
                          aria-label={m.read ? "Read" : "Delivered"}
                          title={m.read
                            ? `Read${m.read_at ? " · " + new Date(m.read_at).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"}) : ""}`
                            : "Delivered"}
                        >
                          {m.read ? "✓✓" : "✓"}
                        </span>
                      )}
                    </button>
                    {Object.keys(grouped).length > 0 && (
                      <div className={`flex gap-1 mt-1 ${mine ? "justify-end" : "justify-start"}`}>
                        {Object.entries(grouped).map(([em, count]) => (
                          <span key={em} className="px-1.5 py-0.5 rounded-full bg-white/10 text-[11px]">
                            {EMOJI_CHARS[em] || em} {count > 1 && count}
                          </span>
                        ))}
                      </div>
                    )}
                    {reactionPickerFor === m.message_id && (
                      <div
                        data-testid={`react-picker-${m.message_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className={`absolute z-30 -top-9 ${mine ? "right-0" : "left-0"} bg-[#1a1a1a] border border-white/10 rounded-full px-2 py-1 flex items-center gap-1 shadow-xl`}
                      >
                        {EMOJI_SET.map((em) => (
                          <button
                            key={em}
                            data-testid={`react-${m.message_id}-${em}`}
                            onClick={() => react(m.message_id, em)}
                            className="text-lg hover:scale-125 transition-transform"
                          >
                            {EMOJI_CHARS[em]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="p-3 border-t border-white/10 flex items-center gap-2">
        <input
          ref={imgInputRef}
          type="file"
          accept="image/*"
          hidden
          data-testid="msg-image-input"
          onChange={(e) => handleImage(e.target.files?.[0])}
        />
        <button
          data-testid="msg-image-btn"
          type="button"
          onClick={() => imgInputRef.current?.click()}
          disabled={sending}
          className="p-2 rounded-md hover:bg-white/10 text-white/70 disabled:opacity-40"
        >
          <ImageIcon size={20} weight="bold" />
        </button>
        {!recording ? (
          <button
            data-testid="msg-voice-btn"
            type="button"
            onClick={startRecording}
            disabled={sending}
            className="p-2 rounded-md hover:bg-white/10 text-white/70 disabled:opacity-40"
          >
            <Microphone size={20} weight="bold" />
          </button>
        ) : (
          <button
            data-testid="msg-voice-stop"
            type="button"
            onClick={stopRecording}
            className="p-2 rounded-md bg-red-500/20 text-red-300 animate-pulse"
          >
            <Stop size={20} weight="fill" />
          </button>
        )}
        <Input
          data-testid="message-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={recording ? "Recording…" : "Type a message…"}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          disabled={recording}
          className="bg-white/5 border-white/10 text-white flex-1"
        />
        <Button
          data-testid="message-send-btn"
          onClick={send}
          disabled={sending || !text.trim()}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <PaperPlaneTilt size={18} weight="fill" />
        </Button>
      </div>
    </div>
  );
}

export default function Inbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const active = searchParams.get("with");

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useAdaptiveInterval(loadThreads, 30000);  // Threads list: was 15s → 30s

  const select = (uid) => setSearchParams({ with: uid });
  const back = () => setSearchParams({});

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6" data-testid="inbox-page">
      <div className="mb-4">
        <h1 className="font-heading text-3xl md:text-4xl font-black flex items-center gap-3">
          <ChatCircleDots size={32} weight="fill" className="text-blue-400" />
          Inbox
        </h1>
        <p className="text-white/60 mt-1">Direct messages with other creators.</p>
        <div className="flex gap-2 mt-4" data-testid="inbox-tabs">
          <span className="h-9 px-4 rounded-full bg-white text-black text-sm font-bold inline-flex items-center" data-testid="inbox-tab-messages">Messages</span>
          <Link to="/market" data-testid="inbox-tab-market"
            className="h-9 px-4 rounded-full bg-sky-400 text-sky-950 text-sm font-bold inline-flex items-center gap-1.5 hover:bg-sky-300 transition-colors">
            <Storefront size={16} weight="fill" /> Tainapp Market
          </Link>
        </div>
      </div>

      <Card className="bg-[#121212] border-white/10 overflow-hidden">
        <CardContent className="p-0">
          <div className="grid md:grid-cols-[280px_1fr]">
            <div className={`border-r border-white/10 ${active ? "hidden md:block" : "block"}`}>
              <ThreadList threads={threads} activeId={active} onSelect={select} />
            </div>
            <div className={active ? "block" : "hidden md:flex md:items-center md:justify-center md:min-h-[400px] text-white/40"}>
              {active ? (
                <Conversation otherUserId={active} onBack={back} refreshThreads={loadThreads} />
              ) : (
                <p className="text-sm">Select a conversation</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
